import React, { useState, useEffect, useRef, useMemo } from 'react';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInAnonymously,
  signInWithCustomToken,
  onAuthStateChanged,
} from 'firebase/auth';
import {
  getFirestore,
  collection,
  addDoc,
  query,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  doc,
  where,
  getDocs,
} from 'firebase/firestore';
import {
  getUrlContext,
  initiate_interval_tree,
} from '../util/interval_tree.ts';

import { auth, db } from '../config/firebase';
import ReactMarkdown from 'react-markdown';
import { bookHistory, key_bookUrl } from '../util/constants';
import {
  getServerBookUrlFromUrl,
  useOpenBook,
  updateLinkTitles,
} from '../util/util';
import { useNavigate } from 'react-router-dom';
import SignupLoginDialog from './SignupDialog';

// --- Mock Data for Fuse.js ---
const bookTitles = [
  'Castes in India',
  'Annihilation of Caste',
  'Maharashtra as a Linguistic Province',
  'Need for Checks and Balances',
  'Thoughts on Linguistic States',
  'Ranade, Gandhi and Jinnah,',
  'Evidence before the Southborough Committee,',
  'Federation versus Freedom',
  'Communal Deadlock and a Way to solve It',
  'States and Minorities',
  'Small Holdings in India,',
  'Mr. Russell and the Reconstruction of Society',
  'In the Bombay Legislature',
  'Dr Ambedkar With the Simon Commission',
  'At the Round Table Conferences',
  'Philosophy of Hinduism',
  'India and Pre-Requisites of Communism',
  'Revolution and Counter-Revolution in Ancient India',
  'Buddha or Karl Marx',
  'Riddles in Hinduism',
  "Untouchables or the Children of India's Ghetto",
  'Essays on Untouchables',
  'Essays on Untouchables- Social',
  'Essays on Untouchables- Political',
  'Essays on Untouchables- Religious',
  'Administration and Finance of The East India Company',
  'The Evolution of Provincial Finance in British India',
  'Miscellaneous Essays.',
  'Pakistan or the Partition of India',
  'What Congress and Gandhi have done to the Untouchables ?',
  'Mr. Gandhi and the Emancipation of the Untouchables',
  'The Buddha and his Dhamma',
  'Pali and other Sources of the Buddha & his Dhamma with an Index',
  'Ancient Indian Commerce',
  'The Untouchables and the Pax Britannica',
  'Lectures on the English Constitution',
  'The Notes on Acts and Laws;',
  'Waiting for a Visa',
  'Who were the Shudras ? - How they came to be the Fourth Varna in the Indo-Aryan Society ?',
  'The Untouchables - Who were They and Why They Became Untouchables ?',
  "Dr. Ambedkar as Member of the Governor-General's Executive Council (1942-46)",
  'Part One: General Discussion on the Draft (17th November 1947 to 14th December 1950)',
  'Hindu Code Bill Clause by Clause Discussion (5th February 1951 to 25th September 1951)',
  'Other Miscellaneous Essays (eg: Manu and the Shudras)',
  'The Principal Architect of the Constitution of India',
  'Dr. Ambedkar and the Hindu Code Bill',
  "Dr. Ambedkar as free India's first Law Minister",
  'The Pali Dictionary',
  'Pali into English, Marathi, Hindi and Gujarathi',
  'Bouddha Pooja Path',
  'Mahad Satyagraha',
  'I have no Homeland',
  'Bringing the Untouchables on Political Horizon',
  'Temple Entry',
  'Socio-Political, Religious Activities',
  "Babasaheb's Speeches",
  'What Way Emancipation/What Path to Salvation',
  'Poona Pact',
  '22 Vows Speech',
  ' ',
];

// --- Stopwords to ignore for autocomplete ---
const stopwords = new Set([
  'a',
  'about',
  'above',
  'after',
  'again',
  'against',
  'all',
  'am',
  'an',
  'and',
  'any',
  'are',
  'as',
  'at',
  'be',
  'because',
  'been',
  'before',
  'being',
  'below',
  'between',
  'both',
  'but',
  'by',
  'can',
  'did',
  'do',
  'does',
  'doing',
  'down',
  'during',
  'each',
  'few',
  'for',
  'from',
  'further',
  'had',
  'has',
  'have',
  'having',
  'he',
  'her',
  'here',
  'hers',
  'herself',
  'him',
  'himself',
  'his',
  'how',
  'i',
  'if',
  'in',
  'into',
  'is',
  'it',
  'its',
  'itself',
  'just',
  'me',
  'more',
  'most',
  'my',
  'myself',
  'no',
  'nor',
  'not',
  'now',
  'of',
  'off',
  'on',
  'once',
  'only',
  'or',
  'other',
  'our',
  'ours',
  'ourselves',
  'out',
  'over',
  'own',
  's',
  'same',
  'she',
  'should',
  'so',
  'some',
  'such',
  't',
  'than',
  'that',
  'the',
  'their',
  'theirs',
  'them',
  'themselves',
  'then',
  'there',
  'these',
  'they',
  'this',
  'those',
  'through',
  'to',
  'too',
  'under',
  'until',
  'up',
  'very',
  'was',
  'we',
  'were',
  'what',
  'when',
  'where',
  'which',
  'while',
  'who',
  'whom',
  'why',
  'will',
  'with',
  'you',
  'your',
  'yours',
  'yourself',
  'yourselves',
]);

export default function Chatbot() {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [userId, setUserId] = useState(null);
  const [messages, setMessages] = useState([]);
  const unsubscribeRef = useRef(null);
  const inactivityTimerRef = useRef(null);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [thinkingText, setThinkingText] = useState('Thinking');
  const [notification, setNotification] = useState(null);
  const [fuse, setFuse] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);
  const [isAutocompleteEnabled, setIsAutocompleteEnabled] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [isSignupPopupOpen, setIsSignupPopupOpen] = useState(false);

  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const inputRef = useRef(null);
  const selectionTimeoutRef = useRef(null);
  const openBookHandler = useOpenBook();
  initiate_interval_tree();

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/fuse.js/dist/fuse.min.js';
    script.async = true;
    script.onload = () => {
      if (window.Fuse) {
        setFuse(
          new window.Fuse(bookTitles, { threshold: 0.4, minMatchCharLength: 2 })
        );
      }
    };

    document.body.appendChild(script);
    const uid = auth?.currentUser?.uid;
    setUserId(uid);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    const savedSetting = localStorage.getItem('autocompleteEnabled');
    if (savedSetting !== null) {
      setIsAutocompleteEnabled(JSON.parse(savedSetting));
    }
  }, []);

  const handleToggleAutocomplete = (e) => {
    const enabled = e.target.checked;
    setIsAutocompleteEnabled(enabled);
    try {
      localStorage.setItem('autocompleteEnabled', JSON.stringify(enabled));
    } catch (error) {
      console.error(
        'Failed to save autocomplete setting to localStorage:',
        error
      );
    }
  };

  // --- HIGHLIGHT START: Functions to setup and cleanup the listener ---
  const cleanupFirestoreListener = () => {
    if (unsubscribeRef.current) {
      console.log('Firestore listener detached due to inactivity.');
      unsubscribeRef.current(); // Detach the listener
      unsubscribeRef.current = null;
    }
  };

  const setupFirestoreListener = () => {
    // Prevent setting up multiple listeners
    if (unsubscribeRef.current || !db || !userId || isSearching) {
      return;
    }
    console.log('Attaching Firestore listener.');
    const chatCollectionPath = `artifacts/${userId}/conversations`;
    const q = query(
      collection(db, chatCollectionPath),
      orderBy('timestamp', 'desc'),
      limit(20)
    );

    unsubscribeRef.current = onSnapshot(
      q,
      (querySnapshot) => {
        console.log('Received snapshot update.');
        const history = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        if (history.length > 0) {
          setHasAcceptedTerms(true);
        }
        setMessages(history.reverse());
      },
      (error) => {
        console.error('Firestore onSnapshot error:', error);
      }
    );
  };
  // --- HIGHLIGHT END ---

  // --- HIGHLIGHT START: Effect to manage user activity and listener lifecycle ---
  useEffect(() => {
    const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5 minutes

    const resetInactivityTimer = () => {
      // If the listener is detached, bring it back online
      if (!unsubscribeRef.current && userId) {
        setupFirestoreListener();
      }
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = setTimeout(
        cleanupFirestoreListener,
        INACTIVITY_TIMEOUT
      );
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearTimeout(inactivityTimerRef.current);
        cleanupFirestoreListener();
      } else {
        resetInactivityTimer();
      }
    };

    // Only start managing activity if the chat is open
    if (isChatOpen && userId) {
      // Initial setup
      resetInactivityTimer();

      // Add event listeners
      window.addEventListener('mousemove', resetInactivityTimer);
      window.addEventListener('keydown', resetInactivityTimer);
      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Cleanup function
      return () => {
        cleanupFirestoreListener();
        clearTimeout(inactivityTimerRef.current);
        window.removeEventListener('mousemove', resetInactivityTimer);
        window.removeEventListener('keydown', resetInactivityTimer);
        document.removeEventListener(
          'visibilitychange',
          handleVisibilityChange
        );
      };
    } else {
      // If chat is closed, ensure listener is cleaned up
      cleanupFirestoreListener();
    }
  }, [isChatOpen, userId, db, isSearching]); // Re-run when chat is opened/closed or user signs in
  // --- HIGHLIGHT END ---

  // --- HIGHLIGHT START: Function to handle term acceptance and add first message ---
  const handleAcceptTerms = async () => {
    if (!db || !userId) {
      console.error(
        'Cannot accept terms: Firestore not ready or user not authenticated.'
      );
      showNotification('Error: Could not initialize chat. Please refresh.');
      return;
    }

    // Set accepted to true, which will reveal the chat interface.
    setHasAcceptedTerms(true);

    // Add the initial welcome message only if there are no messages yet.
    if (messages.length === 0) {
      const chatCollectionPath = `artifacts/${userId}/conversations`;
      await addDoc(collection(db, chatCollectionPath), {
        text: `Jai Bhim! I am Pradnya, a librarian trainee for this digital open library of Babasaheb Ambedkar's Writings and Speeches. My role is to help you navigate through the website and the literature itself, guiding you to the most relevant resources and summaries of Dr. Ambedkar's profound work. I do not have personal attributes or opinions but am here to assist you in exploring his intellectual legacy.Ask me a question related to the Writings and Speeches of Dr. Babasaheb Ambedkar.`,
        timestamp: serverTimestamp(),
        sender: 'system',
      });
    }
  };
  // --- HIGHLIGHT END ---

  const scrollToBottom = () =>
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  useEffect(() => {
    if (isChatOpen) scrollToBottom();
  }, [messages, isChatOpen]);

  const setFocusToInput = () => {
    // Only focus on larger screens to avoid mobile keyboard issues
    if (window.innerWidth > 768) {
      inputRef.current?.focus();
    }
  };

  useEffect(() => {
    if (isChatOpen) {
      setTimeout(() => {
        setFocusToInput();
      }, 100);
    }
  }, [isChatOpen]);

  useEffect(() => {
    let interval;
    if (isLoading) {
      // scroll thinking text into the view
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      interval = setInterval(() => {
        setThinkingText((prev) =>
          prev.endsWith('...') ? 'Thinking deeply.' : prev + '.'
        );
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [isLoading]);

  useEffect(() => {
    const handleSelectionChange = () => {
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
      }

      const selection = window.getSelection();
      const text = selection.toString().trim();

      // Only process the selection if it's not empty and is inside the chat container
      if (
        text &&
        chatContainerRef.current &&
        chatContainerRef.current.contains(selection.anchorNode)
      ) {
        selectionTimeoutRef.current = setTimeout(() => {
          setSelectedText(text);
        }, 800);
      } else {
        setSelectedText('');
      }
    };

    //document.addEventListener('selectionchange', handleSelectionChange);

    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
      }
    };
  }, []);

  const showNotification = (message) => {
    setNotification(message);
    setTimeout(() => setNotification(null), 3000);
  };

  const handleSuggestionClick = (text) => {
    setInput(text);
    setFocusToInput();
  };

  const BotMessage = ({ text, onRegenerate }) => {
    const [isCopied, setIsCopied] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    // --- HIGHLIGHT START: State to hold the text after processing links ---
    const [processedText, setProcessedText] = useState(text);

    if (typeof processedText !== 'string') return null;

    const suggestionRegex = /\[SUGGESTION\](.*?)\[\/SUGGESTION\]/g;
    const followUpSuggestions = [];
    // --- HIGHLIGHT: Use processedText instead of the original 'text' prop ---
    const textWithoutSuggestions = processedText
      .replace(suggestionRegex, (match, suggestionText) => {
        followUpSuggestions.push(suggestionText);
        return '';
      })
      .trim();

    const endSuggestionIndex = textWithoutSuggestions.lastIndexOf('[SUGGEST');
    const cleanText =
      endSuggestionIndex !== -1
        ? textWithoutSuggestions.slice(0, endSuggestionIndex).trim()
        : textWithoutSuggestions;

    const CHAR_LIMIT = 250;
    const isTooLong = cleanText.length > CHAR_LIMIT;

    const displayText =
      isTooLong && !isExpanded
        ? `${cleanText.substring(0, CHAR_LIMIT)}...`
        : cleanText;

    const handleCopy = () => {
      const textarea = document.createElement('textarea');
      // Ensure the full text is copied, not the truncated version
      textarea.value = cleanText;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    };

    return (
      <div className="relative group">
        <div className="max-w-xs md:max-w-md lg:max-w-lg px-4 py-3 rounded-2xl break-words bg-gray-200 text-gray-800">
          <ReactMarkdown
            components={{
              a: ({ node, ...props }) => (
                <a
                  {...props}
                  onClick={(e) => openBookHandler(e, props.href)}
                  className="text-blue-600 font-bold hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
            }}
          >
            {/* --- HIGHLIGHT: Display the potentially truncated text --- */}
            {displayText}
          </ReactMarkdown>

          {/* --- HIGHLIGHT START: "Read More" / "Read Less" button --- */}
          {isTooLong && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-sm font-bold text-blue-600 hover:underline mt-2 focus:outline-none"
            >
              {isExpanded ? 'Read Less' : 'Read More'}
            </button>
          )}
          {/* --- HIGHLIGHT END --- */}
        </div>
        <div className="flex items-center space-x-2 mt-2">
          <button
            onClick={handleCopy}
            className="p-1.5 bg-gray-300 bg-opacity-50 rounded-full text-gray-700 opacity-100 group-hover:opacity-100 transition-opacity"
          >
            {isCopied ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
            )}
          </button>
          <button
            onClick={onRegenerate}
            className="p-1.5 bg-gray-300 bg-opacity-50 rounded-full text-gray-700 opacity-100 group-hover:opacity-100 transition-opacity"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 4v5h5M20 20v-5h-5"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 9a9 9 0 0114.65-4.65L20 5"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M20 15a9 9 0 01-14.65 4.65L4 19"
              />
            </svg>
          </button>
        </div>
        {followUpSuggestions.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2 justify-start">
            {followUpSuggestions.map((s, i) => (
              <button
                key={i}
                onClick={() => handleSuggestionClick(s)}
                className="text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded-full hover:bg-blue-200 transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInput(value);
    if (!isAutocompleteEnabled) return setSuggestions([]);
    const words = value.split(' ');
    const currentWord = words[words.length - 1];
    if (
      fuse &&
      currentWord.length >= 3 &&
      !stopwords.has(currentWord.toLowerCase())
    ) {
      const results = fuse.search(currentWord).slice(0, 5);
      setSuggestions(results.map((res) => res.item));
      setActiveSuggestionIndex(0);
    } else {
      setSuggestions([]);
    }
  };

  const selectSuggestion = (suggestion) => {
    const words = input.split(' ');
    words.pop();
    const newInput = [...words, suggestion].join(' ') + ' ';
    setInput(newInput);
    setSuggestions([]);
    setFocusToInput();
  };

  const handleKeyDown = (e) => {
    if (
      suggestions.length > 0 &&
      (e.key === 'ArrowDown' || e.key === 'ArrowUp')
    ) {
      e.preventDefault();
      setActiveSuggestionIndex(
        (prev) =>
          (e.key === 'ArrowDown' ? prev + 1 : prev - 1 + suggestions.length) %
          suggestions.length
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (suggestions.length > 0) {
        selectSuggestion(suggestions[activeSuggestionIndex]);
      } else {
        handleSend();
      }
    }
  };

  const handleSearch = async () => {
    if (!db || !userId || !searchTerm.trim()) return;
    setIsLoading(true);
    setIsSearching(true);

    const chatCollectionPath = `artifacts/${userId}/conversations`;
    // Fetch the last 200 messages to search through
    const q = query(
      collection(db, chatCollectionPath),
      orderBy('timestamp', 'desc'),
      limit(200)
    );

    try {
      const querySnapshot = await getDocs(q);
      const allRecentMessages = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Filter these messages on the client side
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      const results = allRecentMessages.filter(
        (msg) =>
          msg.text && msg.text.toLowerCase().includes(lowerCaseSearchTerm)
      );

      //   showNotification('No results found.');
      setSearchResults(results.reverse()); // Show in chronological order
    } catch (error) {
      console.error('Error searching chat history:', error);
      showNotification('Error during search.');
    } finally {
      setIsLoading(false);
    }
  };

  const exitSearchMode = () => {
    setIsSearching(false);
    setSearchTerm('');
    setSearchResults([]);
  };

  const handleNewThread = async () => {
    // if the last message is already a break, do nothing
    if (
      messages.length > 0 &&
      messages[messages.length - 1].text === '/break'
    ) {
      showNotification('Already in a new thread.');
      return;
    }
    if (!db || !userId) return;
    const chatCollectionPath = `/artifacts/${userId}/conversations`;
    await addDoc(collection(db, chatCollectionPath), {
      text: '/break',
      timestamp: serverTimestamp(),
      sender: 'system',
    });
    showNotification('New thread started.');
  };

  const handleSelectionSearch = () => {
    if (selectedText) {
      navigate(
        `?q=${encodeURIComponent(` "${selectedText}"`) + '&searchType=simple'}`
      );
      setSelectedText('');
      setIsChatOpen(false);
    }
  };

  const handleSelection = () => {
    return; //for now, disable selection handling

    if (selectionTimeoutRef.current) {
      clearTimeout(selectionTimeoutRef.current);
    }

    const text = window.getSelection().toString().trim();

    if (text) {
      selectionTimeoutRef.current = setTimeout(() => {
        setSelectedText(text);
      }, 1500);
    } else {
      setSelectedText('');
    }
  };

  const handleRegenerate = async (messageId) => {
    if (isLoading || !db || !userId) return;

    const messageIndex = messages.findIndex((m) => m.id === messageId);
    if (messageIndex < 1 || messages[messageIndex - 1].sender !== 'user')
      return;

    const userMessageToRegenerate = messages[messageIndex - 1];

    setIsLoading(true);

    const API_ENDPOINT =
      'https://r5qak3hi6g.execute-api.ap-south-1.amazonaws.com/chat';
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000);

    try {
      const recentMessagesForApi = messages.slice(0, messageIndex).map((m) => ({
        role: m.sender,
        content: m.text,
      }));
      // Add a special instruction for regeneration
      recentMessagesForApi.push({
        role: 'user',
        content: `SEARCH_ONLY_RESPONSE ${userMessageToRegenerate.text}`,
      });

      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: recentMessagesForApi }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (!response.ok)
        throw new Error(`API request failed: ${response.status}`);
      const data = await response.json();

      // Update the specific bot message document in Firestorex
      const docRef = doc(db, `artifacts/${userId}/conversations`, messageId);
      await updateDoc(docRef, { text: data.response });
    } catch (error) {
      clearTimeout(timeoutId);
      showNotification("Sorry, I couldn't generate a new response.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputFocus = () => {
    if (isSearching) {
      exitSearchMode();
    }
    // On mobile devices, scroll the input into view to keep it visible above the keyboard
    if (window.innerWidth < 768) {
      setTimeout(() => {
        inputRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 300); // A small delay helps ensure the keyboard is fully visible
    }
  };

  const handleSend = async () => {
    if (input.trim() === '' || isLoading) return;
    if (!db || !userId) {
      console.error(
        'Cannot send message: Firestore not ready or user not authenticated.'
      );
      return;
    }

    setSuggestions([]);
    const userMessageText = input;
    setInput('');

    setIsLoading(true);

    const userMessage = {
      text: userMessageText,
      timestamp: serverTimestamp(),
      sender: 'user',
    };

    // Save the user's message. The onSnapshot listener will render it.
    const chatCollectionPath = `artifacts/${userId}/conversations`;
    await addDoc(collection(db, chatCollectionPath), userMessage);

    const API_ENDPOINT =
      'https://r5qak3hi6g.execute-api.ap-south-1.amazonaws.com/chat';
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000);

    try {
      // --- HIGHLIGHT START: Logic to find the last thread break ---
      var lastBreakIndex = messages.findLastIndex((m) => m.text === '/break');

      if (lastBreakIndex < messages.length - 11) {
        // If the last break is more than 10 messages ago, we only send the last 10 messages
        lastBreakIndex = messages.length - 11;
      }
      const recentMessagesForApi = messages
        .slice(lastBreakIndex + 1)
        .map((m) => ({
          role: m.sender,
          content: m.text,
        }));
      // --- HIGHLIGHT END ---
      let messageForApi = userMessageText;

      if (window.location.pathname.includes('/books')) {
        messageForApi += ' \n\n I am current reading ' + document.title;
      }

      recentMessagesForApi.push({ role: 'user', content: messageForApi });

      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: recentMessagesForApi }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (!response.ok)
        throw new Error(`API request failed: ${response.status}`);
      const data = await response.json();
      const updatedData = await updateLinkTitles(data.response);

      const botResponse = {
        text: updatedData,
        timestamp: serverTimestamp(),
        sender: 'assistant',
      };
      await addDoc(collection(db, chatCollectionPath), botResponse);
    } catch (error) {
      clearTimeout(timeoutId);
      const errorMessage =
        'The Librarian has probably stepped away, she will be back soon!';
      console.error('Error in handleSend:', error);
      const errorResponse = {
        text: errorMessage,
        timestamp: serverTimestamp(),
        sender: 'assistant',
      };
      await addDoc(collection(db, chatCollectionPath), errorResponse);
    } finally {
      setIsLoading(false);
      setFocusToInput();
    }
  };

  const handleChatOpen = () => {
    if (!userId) {
      const uid = auth?.currentUser?.uid;
      setUserId(uid);
      if (!uid) {
        setIsSignupPopupOpen(true);
        return;
      }
    }
    setIsChatOpen(true);
    // Maximize automatically on mobile screens
    if (window.innerWidth < 768) {
      setIsMaximized(true);
    }
  };

  return (
    <>
      {/* --- HIGHLIGHT: The chat window is now conditionally rendered to prevent click blocking --- */}
      {isSignupPopupOpen && (
        <SignupLoginDialog
          showSignupPopup={isSignupPopupOpen}
          setIsSignupPopupOpen={setIsSignupPopupOpen}
        />
      )}
      {isChatOpen && (
        <div
          className={`z-[9999] transition-all duration-500 ease-in-out ${isMaximized ? 'fixed inset-0 sm:p-4 flex items-center justify-center' : 'fixed bottom-10 right-2'}`}
        >
          <div
            className={`duration-300 transition-all opacity-100 translate-y-0 ${isMaximized ? 'w-full h-full' : ''}`}
          >
            <div
              className={`bg-white rounded-2xl shadow-2xl flex flex-col duration-500 ease-in-out transition-all ${isMaximized ? 'w-full h-full max-w-4xl mx-auto' : 'w-[calc(100vw-40px)] h-[70vh] sm:w-[400px] sm:h-[600px]'}`}
            >
              <div className="flex-shrink-0">
                <div className="flex justify-between items-center p-2 sm:p-4 border-b flex-shrink-0">
                  <h3 className="font-bold text-md sm:text-lg">
                    Pradnya the Librarian
                  </h3>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setIsMaximized(!isMaximized)}
                      className="p-1 rounded-full hover:bg-gray-200"
                    >
                      {isMaximized ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M10 21h4a2 2 0 002-2v-4a2 2 0 00-2-2h-4a2 2 0 00-2 2v4a2 2 0 002 2zM10 3h4a2 2 0 012 2v4a2 2 0 01-2 2h-4a2 2 0 01-2-2V5a2 2 0 012-2z"
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M4 8V4h4m12 4V4h-4M4 16v4h4m12-4v4h-4"
                          />
                        </svg>
                      )}
                    </button>
                    <button
                      onClick={() => setIsChatOpen(false)}
                      className="p-1 rounded-full hover:bg-gray-200"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="p-3 border-b">
                  <div className="flex justify-between items-center text-sm text-gray-600">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="autocomplete-toggle"
                        checked={isAutocompleteEnabled}
                        onChange={handleToggleAutocomplete}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="autocomplete-toggle">Autocomplete</label>
                    </div>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={handleNewThread}
                        className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded-full hover:bg-gray-300"
                      >
                        New Thread
                      </button>
                      <input
                        type="text"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        className="w-24 p-1 border border-gray-300 rounded-full text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                      <button
                        onClick={handleSearch}
                        className="p-1 text-gray-500 hover:text-blue-600"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                      </button>
                      {isSearching && (
                        <button
                          onClick={exitSearchMode}
                          className="p-1 text-gray-500 hover:text-red-600"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div
                ref={chatContainerRef}
                className="flex-grow p-4 space-y-4 overflow-y-auto flex flex-col"
                onMouseUp={handleSelection}
                onTouchEnd={handleSelection}
              >
                {hasAcceptedTerms ? (
                  <>
                    {(isSearching ? searchResults : messages).map(
                      (msg, index) => {
                        if (msg.text === '/break') {
                          return (
                            <div
                              key={msg.id || index}
                              className="relative py-2"
                            >
                              <div
                                className="absolute inset-0 flex items-center"
                                aria-hidden="true"
                              >
                                <div className="w-full border-t border-gray-300 border-dashed"></div>
                              </div>
                              <div className="relative flex justify-center">
                                <span className="bg-white px-2 text-xs text-gray-500">
                                  New thread started
                                </span>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div
                            key={msg.id || index}
                            className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                          >
                            {msg.sender === 'user' ? (
                              <div className="max-w-xs md:max-w-md lg:max-w-lg px-4 py-3 rounded-2xl break-words bg-blue-600 text-white">
                                {msg.text}
                              </div>
                            ) : (
                              <BotMessage
                                text={msg.text}
                                onRegenerate={() => handleRegenerate(msg.id)}
                              />
                            )}
                          </div>
                        );
                      }
                    )}
                    {isLoading && (
                      <div className="flex justify-start">
                        <div className="bg-gray-200 text-gray-800 rounded-2xl px-4 py-3">
                          <p className="text-sm">{thinkingText}</p>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </>
                ) : (
                  <div className="flex-grow flex flex-col justify-center items-center text-center p-4">
                    <h4 className="font-bold text-lg mb-2">
                      Welcome to Pradnya
                    </h4>
                    <div className="text-sm text-gray-600 space-y-3">
                      <p>
                        <span className="font-semibold text-orange-600">
                          Beta Product Disclaimer:
                        </span>{' '}
                        This is a chat to speak with Pradnya, the AI librarian
                        under training. You may encounter occasional
                        inaccuracies or incomplete responses while she learns.
                        We appreciate your understanding and feedback as we
                        continue to improve the service.
                      </p>
                      <p>
                        {/* You can customize this paragraph */}
                        <span className="font-semibold">
                          About This Library:
                        </span>{' '}
                        Pradnya is specifically trained on the collected
                        "Writings and Speeches of Dr. B. R. Ambedkar." Ask
                        questions about his works, concepts, and ideas to
                        receive guided assistance and find relevant passages.
                        Pradnya's aim is to ensure that you read the original
                        work of Dr. Babasaheb Ambedkar. She will frequently
                        redirect you to the right sources. Take advantage of it
                        and read ORIGINAL!
                      </p>
                    </div>
                    <button
                      onClick={handleAcceptTerms}
                      className="mt-6 bg-blue-600 text-white font-bold py-2 px-6 rounded-full hover:bg-blue-700 transition-colors"
                    >
                      Agree and Start Chat
                    </button>
                  </div>
                )}
              </div>

              <div className="flex-shrink-0 p-3 border-t">
                {selectedText && (
                  <div className="pb-2 flex justify-center">
                    <button
                      onClick={handleSelectionSearch}
                      className="text-sm bg-gray-700 text-white px-3 py-1 rounded-full hover:bg-black transition-colors"
                    >
                      Search for "{selectedText.substring(0, 20)}..."
                    </button>
                  </div>
                )}
                {isAutocompleteEnabled && suggestions.length > 0 && (
                  <div className="border-t mb-2 pt-2">
                    {suggestions.map((s, i) => (
                      <div
                        key={i}
                        onClick={() => selectSuggestion(s)}
                        className={`p-2 rounded-md cursor-pointer text-sm text-gray-700 ${i === activeSuggestionIndex ? 'bg-blue-100' : 'hover:bg-gray-100'}`}
                      >
                        {s}
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center">
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => {
                      if (isSearching) {
                        exitSearchMode();
                      }
                      handleInputFocus();
                    }}
                    placeholder="I want to read about..."
                    className="flex-grow p-2 sm:p-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isLoading || !hasAcceptedTerms}
                  />
                  <button
                    onClick={handleSend}
                    disabled={isLoading || !hasAcceptedTerms}
                    className="ml-2 sm:ml-3 bg-blue-600 text-white p-2 sm:p-3 rounded-full hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 sm:h-6 sm:w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 10l7-7m0 0l7 7m-7-7v18"
                      />
                    </svg>
                  </button>
                </div>
                <div className="text-center text-xs text-gray-400 pt-2">
                  AI can make mistakes! Your responses may be used to train our
                  model!
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- HIGHLIGHT: The button is now independent and only shows when the chat is closed --- */}
      {!isChatOpen && (
        <button
          onClick={handleChatOpen}
          title="Open chat with Pradnya"
          className={`z-[9999] fixed bottom-10 right-5 duration-300 transition-all opacity-100 translate-y-0 bg-blue-600 text-white rounded-full p-3 shadow-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
        </button>
      )}
      {/* slide it slightly down to avoid getting blocked by navigation bar */}
      {notification && (
        <div className="fixed top-12 right-5 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg transition-opacity duration-300">
          {notification}
        </div>
      )}
    </>
  );
}
